import React, { useEffect, useState } from 'react';
import { Paper, Typography, Box, CircularProgress, List, ListItem, ListItemText, Chip, Tooltip, LinearProgress } from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const RANGE_LABEL = { '24h': 'últimas 24h', '7d': 'últimos 7 dias', '30d': 'últimos 30 dias' };

/**
 * Volume de logs Syslog (Pegeia) por fonte.
 * Cache backend = 5 min. Conexão a cada source com timeout 5s.
 */
export default function SyslogVolumeCard({ range = '24h' }) {
    const navigate = useNavigate();
    const [state, setState] = useState({ loading: true });

    useEffect(() => {
        setState((s) => ({ ...s, loading: true }));
        api.get(`/dashboard/pegeia/sources_volume?range=${range}`)
            .then((res) => setState({ ...res.data, loading: false }))
            .catch(() => setState({ loading: false, items: [], total: 0, sources_total: 0, sources_ok: 0 }));
    }, [range]);

    const max = Math.max(1, ...((state.items || []).filter(i => i.count >= 0).map((i) => i.count)));

    return (
        <Paper
            elevation={2}
            sx={{
                m: 2,
                p: 2,
                minHeight: 280,
                display: 'flex',
                flexDirection: 'column',
                borderLeft: '4px solid #e01b29',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StorageIcon color="primary" />
                    <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                        Volume Syslog — {RANGE_LABEL[range]}
                    </Typography>
                </Box>
                {!state.loading && state.total != null && (
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        {state.total.toLocaleString('pt-BR')}
                    </Typography>
                )}
            </Box>

            {!state.loading && state.sources_total > 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1.5 }}>
                    {state.sources_ok}/{state.sources_total} fonte(s) responderam · cache de 5min
                </Typography>
            )}

            {state.loading ? (
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={28} />
                </Box>
            ) : !state.items || state.items.length === 0 ? (
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Nenhuma fonte cadastrada
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, overflow: 'auto', maxHeight: 240 }}>
                    {state.items.map((s) => {
                        const isError = s.status !== 'ok';
                        const fillPct = s.count > 0 ? Math.min(100, (s.count / max) * 100) : 0;
                        return (
                            <Box
                                key={s.source_id}
                                onClick={() => navigate('/pegeia_source')}
                                sx={{ cursor: 'pointer', p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontFamily: 'Montserrat', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {s.source_name}
                                        </Typography>
                                        {isError && (
                                            <Tooltip title={s.error_msg || s.status}>
                                                <ErrorOutlineIcon fontSize="small" color="error" />
                                            </Tooltip>
                                        )}
                                    </Box>
                                    {isError ? (
                                        <Chip size="small" label={s.status} color="error" variant="outlined" />
                                    ) : (
                                        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, ml: 1 }}>
                                            {s.count.toLocaleString('pt-BR')}
                                        </Typography>
                                    )}
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={fillPct}
                                    sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        bgcolor: '#f0f0f0',
                                        '& .MuiLinearProgress-bar': { bgcolor: isError ? '#bdbdbd' : '#e01b29' },
                                    }}
                                />
                                {s.last_event && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                                        último evento: {new Date(s.last_event).toLocaleString('pt-BR')}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Paper>
    );
}
